import dayjs from './dayjs.js'
import { formats } from "../constants/formats.js"

export const formatPrice = (price, currency, fractionDigits = 2) => {
    if (price == null) {
        return ''
    }
    try {
        const currencyCode = currency?.toUpperCase()
        const locale = (currencyCode === 'GBP') ? 'en-UK' : currencyCode === 'USD' ? 'en-US' : 'nl-NL'
        const money = Intl.NumberFormat(locale,
            {
                style: 'currency',
                currency: currencyCode,
                minimumFractionDigits: fractionDigits,
                maximumFractionDigits: fractionDigits,
            }
        ).format([price])
        return money
    }
    catch (error) {
        console.debug({ price, currency })
        console.error(error)
        return '⚠' + price
    }
}

export const formatDate = (stringDate) => {
    if (!stringDate) {
        return ''
    }
    return dayjs(stringDate, formats.backendDateFormat).format(formats.frontendDateFormat)
}

export const formatDateTime = (stringDate) => {
    if (!stringDate) {
        return ''
    }
    const isUTC = /(?:Z|[+-]\d{2}:?\d{2})$/.test(stringDate);
    if (isUTC) {
        return dayjs.utc(stringDate).local().format(formats.frontendDateTimeFormat);
    } else {
        return dayjs(stringDate).format(formats.frontendDateTimeFormat);
    }
}

export const formatBackendDate = (stringDate) => {
    if (!stringDate) {
        return ''
    }
    return dayjs(stringDate, formats.frontendDateFormat).format(formats.backendDateFormat)
}

export const formatTodayBackendDate = () => dayjs().format(formats.backendDateFormat)

export const getTimeAgo = (stringDate, smart = false) => {
    const date = dayjs(stringDate, formats.backendDateTimeFormat)
    if (!smart || dayjs().diff(date, 'hours') < 24) {
        return date.fromNow()
    }
    return date.format(formats.frontendDateTimeFormat)
}

export const DateFormatted = (date) => {
    const dateObj = new Date(date)
    if (isNaN(dateObj)) {
        return "NA"
    }
    return new Intl.DateTimeFormat('de-DE').format(dateObj)
}


export const AddOrSubractDays = (startingDate, number, add) => {
    const date = new Date(startingDate)
    date.setDate(date.getDate() + (add ? number : -number))
    return date
}


export const DifferenceBetweenDates = (date1, date2) => {
    var d1 = new Date(date1);
    var d2 = new Date(date2);
    console.log(date1);
    console.log(date2);
    var diffDays = parseInt((d2 - d1) / (1000 * 60 * 60 * 24), 10);

    return diffDays
}

export const DateDifference = (date1, date2, unit = 'day') => {
    const d1 = dayjs(date1)
    return d1.diff(date2, unit)
}

export const getDateDiffAsMessage = (date1, date2) => {
    const diff = DateDifference(date1, date2)
    if (diff < 0) {
        return `${Math.abs(diff)} days late`
    }
    if (diff > 0) {
        return `${diff} days ahead`
    }
    if (diff === 0) {
        return 'on time'
    }
}

export const formatNumber = (value, locale = 'nl-NL') => {
    return Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value)
}

export const convertToERPCurrency = (val, rate) => val * rate
export const convertFromERPCurrency = (val, rate) => val / rate

export const roundOfNumber = (val, precision) => {
    if (isNaN(val)) {
        // Handle invalid input
        return 0;
    }
    if (isNaN(precision) || precision < 0) {
        return Math.round(val)
    }
    return Number(val?.toFixed(precision))
}

export const localizeNumber = (val, locale, precision = 2) => {
    if (val == null || val === '') {
        return null
    }
    if (!val)
        return 0;

    return Intl.NumberFormat(locale, { maximumFractionDigits: precision }).format(val)
}

export const parseLocalizedNumber = (stringNumber, _locale = 'nl-NL') => {
    if (stringNumber == null || stringNumber === '') {
        return 0
    }
    if (typeof stringNumber === 'number') {
        return stringNumber
    }

    let str = stringNumber.toString().trim()
    str = str.replace(/[^\d.,-]/g, '')
    if (!str) return 0

    const hasDot = str.includes('.')
    const hasComma = str.includes(',')

    if (hasDot && hasComma) {
        const lastDot = str.lastIndexOf('.')
        const lastComma = str.lastIndexOf(',')
        if (lastComma > lastDot) {
            // EU format: 1.234,56
            str = str.replace(/\./g, '').replace(',', '.')
        } else {
            // US/UK format: 1,234.56
            str = str.replace(/,/g, '')
        }
    } else if (hasComma) {
        str = str.replace(/,/g, '.')
    }

    const num = parseFloat(str)
    return isNaN(num) ? 0 : num
}

/**
 * Check if a date is on a weekend (Saturday or Sunday)
 * @param {string|Date|Dayjs} date
 * @returns {boolean}
 */
export const isWeekend = (date) => {
    const d = dayjs(date);
    return d.day() === 0 || d.day() === 6;
};

/**
 * @param {string|Date|Dayjs} date - The date to subtract from.
 * @param {number} days - The number of business days to subtract (excluding weekends).
 * @param {string} inputDateFormat - The format of the input date string.
 * @param {string} outputDateFormat - The format of the output date string.
 * @param {string|Date|Dayjs} [minDate=today] - The minimum allowable date (defaults to today).
 * @param {boolean} [excludeWeekends=true] - Whether to exclude Saturday and Sunday.
 * @returns {string} - The resulting date string, formatted according to outputDateFormat.
 */
export function subtractDays(date, days, inputDateFormat = formats.backendDateFormat, outputDateFormat = formats.backendDateFormat, minDate = dayjs().startOf('day'), excludeWeekends = true) {
    // If the input date is null, return the current date
    if (!date) {
        return dayjs().format(outputDateFormat);
    }

    // Parse the input date
    let current = dayjs(date, inputDateFormat);
    if (!current.isValid()) {
        return dayjs().format(outputDateFormat);
    }

    if (excludeWeekends) {
        let remainingDays = days;
        while (remainingDays > 0) {
            current = current.subtract(1, 'day');
            // Dayjs: 0 = Sunday, 6 = Saturday
            if (current.day() !== 0 && current.day() !== 6) {
                remainingDays--;
            }
        }
        // If current date falls on weekend (e.g. days was 0 and date was a weekend), adjust to Friday
        while (current.day() === 0 || current.day() === 6) {
            current = current.subtract(1, 'day');
        }
    } else {
        current = current.subtract(days, 'days');
    }

    // Parse the minimum date
    let minMoment = dayjs.isDayjs(minDate) ? minDate.startOf('day') : dayjs(minDate, inputDateFormat).startOf('day');
    if (excludeWeekends) {
        // If minMoment is on a weekend, adjust to Monday
        while (minMoment.day() === 0 || minMoment.day() === 6) {
            minMoment = minMoment.add(1, 'day');
        }
    }

    // Ensure the result is not less than the minimum date
    const resultDate = current.isBefore(minMoment) ? minMoment : current;

    // Return the formatted result
    const formattedResult = resultDate.format(outputDateFormat);

    return formattedResult;
}
